import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Bell, Filter, Search, CheckCircle, RefreshCw } from 'lucide-react';
import { Notification } from '../types';
import { CleanNotificationItem } from '../components/Layout/CleanNotificationItem';
import { NotificationDetailModal } from '../components/UI/NotificationDetailModal';
import { logInfo } from '../utils/logger';
import { notificationService } from '../services/notificationService';
import { useAuth } from '../contexts/AuthContext';

export const NotificationsSection: React.FC = () => {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const [notifications, setNotifications] = useState<Notification[]>(() => notificationService.getNotifications());
  const [hasMoreNotifications, setHasMoreNotifications] = useState(() => notificationService.hasMore());
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const loadNotifications = useCallback(async () => {
    setNotificationsLoading(true);
    notificationService.resetPagination();
    try {
      const result = await notificationService.loadFromDatabase(user?.organizationId ?? undefined, user?.id, 1, 50);
      setNotifications(notificationService.getNotifications());
      setHasMoreNotifications(result.hasMore);
    } finally {
      setNotificationsLoading(false);
    }
  }, [user?.id, user?.organizationId]);

  useEffect(() => {
    const unsubscribe = notificationService.subscribe(setNotifications);
    void loadNotifications();
    return unsubscribe;
  }, [loadNotifications]);

  const loadMoreNotifications = useCallback(async () => {
    setNotificationsLoading(true);
    try {
      const hasMore = await notificationService.loadMore(user?.organizationId ?? undefined, user?.id);
      setNotifications(notificationService.getNotifications());
      setHasMoreNotifications(hasMore);
      return hasMore;
    } finally {
      setNotificationsLoading(false);
    }
  }, [user?.id, user?.organizationId]);

  const markAllNotificationsAsRead = useCallback(async () => {
    await notificationService.markAllAsRead();
    setNotifications(notificationService.getNotifications());
  }, []);

  // Handle loading more notifications
  const handleLoadMore = useCallback(async () => {
    if (hasMoreNotifications && !notificationsLoading) {
      logInfo('NotificationsSection', 'Loading more notifications...');
      await loadMoreNotifications();
    }
  }, [hasMoreNotifications, notificationsLoading, loadMoreNotifications]);

  // Handle scroll to load more notifications
  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const scrollThreshold = 100; // Load more when 100px from bottom

    if (scrollHeight - scrollTop - clientHeight < scrollThreshold) {
      handleLoadMore();
    }
  }, [handleLoadMore]);

  useEffect(() => {
    const filter = params.get('filter');
    if (filter === 'maintenance') {
      setSearchTerm('maintenance');
    }
  }, []);

  const filteredNotifications = useMemo(() => notifications.filter(notification => {
    const matchesSearch = notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || notification.category === typeFilter;
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'read' && notification.read) ||
      (statusFilter === 'unread' && !notification.read);

    return matchesSearch && matchesType && matchesStatus;
  }), [notifications, searchTerm, typeFilter, statusFilter]);

  // Group notifications by date
  const groupedNotifications = useMemo(() => {
    const groups: { [key: string]: Notification[] } = {};

    filteredNotifications.forEach(notification => {
      const notificationDate = new Date(notification.createdAt);
      const dateKey = notificationDate.toISOString().split('T')[0]; // YYYY-MM-DD format

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(notification);
    });

    // Sort dates in descending order (newest first)
    const sortedGroups: { [key: string]: Notification[] } = {};
    Object.keys(groups)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
      .forEach(dateKey => {
        // Sort notifications within each date by creation time (newest first)
        sortedGroups[dateKey] = groups[dateKey].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      });

    return sortedGroups;
  }, [filteredNotifications]);

  // Helper function to format date labels
  const formatDateLabel = (dateKey: string): string => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const notificationDate = new Date(dateKey);
    const todayStr = today.toISOString().split('T')[0];
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    if (dateKey === todayStr) {
      return '📅 Today';
    } else if (dateKey === yesterdayStr) {
      return '🕒 Yesterday';
    } else {
      // Format as "Monday, Jan 1, 2026"
      return notificationDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  const totalCount = notifications.length;

  const handleMarkAllAsRead = async () => {
    await markAllNotificationsAsRead();
  };


  const handleNotificationClick = (notification: Notification) => {
    // Open detail modal
    setSelectedNotification(notification);
    setIsDetailModalOpen(true);
    logInfo('NotificationsSection', 'Notification clicked', { notificationId: notification.id, category: notification.category });
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedNotification(null);
  };

  // Handle refresh notifications
  const handleRefresh = useCallback(async () => {
    logInfo('NotificationsSection', 'Refreshing notifications...');
    try {
      await loadNotifications();
    } catch (error) {
      console.error('Failed to refresh notifications:', error);
    }
  }, [loadNotifications]);



  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-header">Notification Center</h1>
          <p className="page-subtitle">
            Manage and view all your platform notifications
          </p>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-secondary">
            <Bell className="w-4 h-4" />
            <span>{unreadCount} unread</span>
            <span className="text-tertiary">•</span>
            <span>{totalCount} total</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={notificationsLoading}
              className="flex items-center gap-2 px-3 py-2 btn-secondary hover:btn-secondary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              title="Refresh notifications"
            >
              <RefreshCw className={`w-4 h-4 ${notificationsLoading ? 'animate-spin' : ''}`} />
              {notificationsLoading ? 'Refreshing...' : 'Refresh'}
            </button>

            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="flex items-center gap-2 px-4 py-2 btn-secondary hover:btn-secondary-hover transition-all duration-200"
              >
                <CheckCircle className="w-4 h-4" />
                Mark all as read
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-light rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 futuristic-input"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-secondary-400" />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 border border-light rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 futuristic-input"
            >
              <option value="all">All Types</option>
              <option value="SECURITY_ALERT">Security Alerts</option>
              <option value="SAFETY_ALERT">Safety Alerts</option>
              <option value="MAINTENANCE_REMINDER">Maintenance</option>
              <option value="DEVICE_ASSIGNMENT">Asset Assignment</option>
              <option value="DEVICE_OFFLINE">Asset Status</option>
              <option value="PERFORMANCE_ALERT">Performance</option>
              <option value="RULE_TRIGGERED">Rules</option>
              <option value="TEMPERATURE_ALERT">Sensor Alerts</option>
              <option value="SYSTEM_UPDATE">System Updates</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-light rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 futuristic-input"
            >
              <option value="all">All Status</option>
              <option value="unread">Unread</option>
              <option value="read">Read</option>
            </select>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="relative">
        <div
          ref={scrollContainerRef}
          className="card overflow-hidden max-h-[600px] overflow-y-auto"
          onScroll={handleScroll}
        >
          {filteredNotifications.length === 0 ? (
            <div className="p-12 text-center">
              <Bell className="w-12 h-12 text-secondary-400 mx-auto mb-4" />
              <h3 className="subsection-header">No notifications found</h3>
              <p className="text-secondary">
                {notifications.length === 0
                  ? "You don't have any notifications yet."
                  : "No notifications match your current filters."
                }
              </p>
            </div>
          ) : (
            <>

              {/* Date-wise Grouped Notifications */}
              <div className="space-y-8 px-4">
                {Object.entries(groupedNotifications).map(([dateKey, notifications]) => (
                  <div key={dateKey} className="px-4 py-6">
                    <div className="pb-4 border-b-2 border-pink-500">
                      <h3 className="text-lg font-bold text-pink-600">
                        {formatDateLabel(dateKey)} ({notifications.length})
                      </h3>
                    </div>
                    <div className="mt-6 divide-y divide-slate-200">
                      {notifications.map((notification) => (
                        <CleanNotificationItem
                          key={notification.id}
                          notification={notification}
                          onClick={handleNotificationClick}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Pagination Controls */}
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6 text-sm text-secondary">
              <span>
                Showing <span className="font-semibold text-primary">{filteredNotifications.length}</span> of{' '}
                <span className="font-semibold text-primary">{totalCount}</span> notifications
              </span>
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  Unread: <span className="font-semibold">{unreadCount}</span>
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                  Read: <span className="font-semibold">{totalCount - unreadCount}</span>
                </span>
              </div>
            </div>

            {/* Load More Button */}
            {hasMoreNotifications && !notificationsLoading && (
              <button
                onClick={handleLoadMore}
                className="flex items-center gap-2 px-4 py-2 btn-primary hover:btn-primary-hover transition-all duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
                Load More
              </button>
            )}
          </div>

          {/* End of list indicator */}
          {!hasMoreNotifications && filteredNotifications.length > 0 && (
            <div className="flex items-center justify-center py-6 border-t border-light bg-gradient-to-r from-transparent via-light/30 to-transparent">
              <div className="flex items-center gap-2 text-secondary">
                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm font-semibold">All notifications loaded</span>
              </div>
            </div>
          )}

          {/* No notifications state */}
          {filteredNotifications.length === 0 && notifications.length > 0 && (
            <div className="flex items-center justify-center py-6 border-t border-light">
              <span className="text-sm text-secondary font-medium">No notifications match your filters</span>
            </div>
          )}
        </div>

        {/* Loading indicator at bottom of scroll container */}
        {notificationsLoading && hasMoreNotifications && (
          <div className="flex items-center justify-center py-6 border-t border-light bg-gradient-to-r from-transparent via-light/50 to-transparent">
            <div className="flex items-center gap-3 text-primary">
              <div className="w-6 h-6 border-3 border-primary border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm font-semibold">Loading more notifications...</span>
            </div>
          </div>
        )}

        {/* Scroll hint for pagination */}
        {hasMoreNotifications && !notificationsLoading && filteredNotifications.length > 10 && (
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-primary/90 text-white px-3 py-1 rounded-full text-xs font-medium shadow-lg animate-pulse">
            Scroll down or click Load More
          </div>
        )}
      </div>

      {/* Notification Detail Modal */}
      <NotificationDetailModal
        notification={selectedNotification}
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
      />

    </div>
  );
};
